import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Instagram, Facebook, Linkedin } from "lucide-react";
import { LOGO_URL, MAPS_EMBED_URL, INSTAGRAM_URL } from "@/lib/assets";
import { useLang } from "@/context/LangContext";
import { useChrome } from "@/lib/guChrome";
import { GoogleRatingBadge } from "@/components/GoogleReviews";

const quickLinks = [
  { to: "/about", label: "About Us" },
  { to: "/general-psychiatry", label: "General Psychiatry" },
  { to: "/conditions/depression", label: "Depression Treatment" },
  { to: "/conditions/anxiety", label: "Anxiety & Panic" },
  { to: "/conditions/ocd", label: "OCD Treatment" },
  { to: "/conditions/insomnia", label: "Insomnia Treatment" },
  { to: "/conditions/bipolar", label: "Bipolar Disorder" },
  { to: "/conditions/adhd-children", label: "ADHD in Children" },
  { to: "/conditions/schizophrenia", label: "Schizophrenia & Psychosis" },
  { to: "/conditions/stress-adjustment", label: "Stress & Adjustment" },
  { to: "/conditions/somatoform", label: "Body Pain & Headache" },
  { to: "/conditions/dementia", label: "Dementia & Memory Loss" },
  { to: "/conditions/womens-mental-health", label: "Women's Mental Health" },
  { to: "/conditions/suicide-crisis", label: "Suicide Risk & Crisis Care" },
  { to: "/conditions/epilepsy-depression", label: "Epilepsy with Depression" },
  { to: "/conditions/post-stroke-depression", label: "Post-Stroke Depression" },
  { to: "/therapies/rtms", label: "rTMS Treatment" },
  { to: "/therapies/mbpt", label: "Modified Brief Pulse Therapy" },
  { to: "/therapies/tdcs", label: "tDCS Treatment" },
  { to: "/therapies/ketamine-infusion", label: "Ketamine IV Infusion" },
  { to: "/therapies/naltrexone-implant", label: "Naltrexone Implant" },
  { to: "/gu/general-psychiatry", label: "ગુજરાતીમાં વાંચો" },
  { to: "/gu/deaddiction", label: "વ્યસનમુક્તિ (ગુજરાતી)" },
  { to: "/gu/sexology", label: "જાતીય સ્વાસ્થ્ય (ગુજરાતી)" },
  { to: "/gu/neuromodulation", label: "rTMS · ન્યુરોમોડ્યુલેશન (ગુજરાતી)" },
  { to: "/neuropsychiatry", label: "Neuropsychiatry" },
  { to: "/gu/neuropsychiatry", label: "ન્યુરોસાઇકિયાટ્રી (ગુજરાતી)" },
  { to: "/deaddiction", label: "Deaddiction" },
  { to: "/neuromodulation", label: "Neuromodulation" },
  { to: "/sexology", label: "Sexology" },
  { to: "/case-reports", label: "Case Reports" },
  { to: "/articles", label: "Articles" },
  { to: "/blog", label: "Blog" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
  { to: "/for-physicians", label: "For Physicians" },
];

export default function Footer() {
  const { t } = useLang();
  const { g, route, isGu } = useChrome();
  return (
    <footer data-testid="site-footer" className="bg-[#2D2623] text-[#FDFBF7] relative overflow-hidden">
      <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-24 relative">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <Link to="/" className="flex items-center gap-4 mb-6">
              <span className="w-14 h-14 rounded-2xl bg-[#FDFBF7] flex items-center justify-center overflow-hidden shadow-[0_8px_24px_rgba(0,0,0,0.25)]">
                <img src={LOGO_URL} alt="Manas Hospital logo" className="w-full h-full object-contain p-1" />
              </span>
              <span className="flex flex-col leading-none">
                <span className="font-display text-3xl">Manas</span>
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#A39171] mt-1">
                  {g("Neuropsychiatry · Since 1989")}
                </span>
              </span>
            </Link>
            <p className="text-[#FDFBF7]/70 max-w-md leading-relaxed">
              {g("A premium psychiatric care facility offering deaddiction services, sex therapy, and interventional neuromodulation. Evidence-based, empathetic, exceptional.")}
            </p>

            <div className="mt-7">
              <GoogleRatingBadge />
            </div>

            <div className="flex items-center gap-3 mt-8">
              {[
                { Icon: Instagram, label: "instagram", url: INSTAGRAM_URL },
                { Icon: Facebook, label: "facebook", url: "" },
                { Icon: Linkedin, label: "linkedin", url: "" },
              ]
                .filter((s) => s.url)
                .map(({ Icon, label, url }) => (
                  <a
                    key={label}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`footer-social-${label}`}
                    aria-label={`Manas Hospital on ${label}`}
                    title={`Manas Hospital on ${label}`}
                    className="w-11 h-11 rounded-full border border-[#FDFBF7]/15 flex items-center justify-center hover:bg-[#E07A5F] hover:border-[#E07A5F] transition-all"
                  >
                    <Icon className="w-4 h-4" />
                  </a>
                ))}
            </div>
          </div>

          <div className="md:col-span-3">
            <p className="eyebrow text-[#E2C044] mb-5">{g("Quick Links")}</p>
            <ul className="flex flex-col gap-3 text-[15px]">
              {quickLinks.filter((l) => !(isGu && l.to.startsWith("/gu/"))).map((l) => (
                <li key={l.to}>
                  <Link to={route(l.to)} className="text-[#FDFBF7]/80 hover:text-[#E07A5F] transition-colors">
                    {g(l.label)}
                  </Link>
                </li>
              ))}
              <li><Link to="/admin/login" className="text-[#FDFBF7]/40 hover:text-[#E07A5F] transition-colors text-xs">Admin</Link></li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <p className="eyebrow text-[#E2C044] mb-5">{g("Reach Us")}</p>
            <ul className="flex flex-col gap-4 text-[15px] text-[#FDFBF7]/80">
              <li className="flex gap-3">
                <MapPin className="w-5 h-5 mt-0.5 text-[#E07A5F] shrink-0" />
                <span>{g("2nd floor, Carlton Square, Near Madhavdeep Complex,")}<br/>{g("Kalanala, Bhavnagar — 364001")}</span>
              </li>
              <li>
                <a href="tel:+919427560101" className="flex gap-3 hover:text-[#E07A5F] transition-colors">
                  <Phone className="w-5 h-5 mt-0.5 text-[#E07A5F]" />
                  +91 94275 60101
                </a>
              </li>
              <li>
                <a href="mailto:manasneuropsychiatryhospital@gmail.com" className="flex gap-3 hover:text-[#E07A5F] transition-colors break-all">
                  <Mail className="w-5 h-5 mt-0.5 text-[#E07A5F] shrink-0" />
                  manasneuropsychiatryhospital@gmail.com
                </a>
              </li>
            </ul>

            {/* Embedded map */}
            <div
              data-testid="footer-map"
              className="mt-6 rounded-2xl overflow-hidden border border-[#FDFBF7]/15 aspect-[4/3] bg-[#FDFBF7]/5"
            >
              <iframe
                title="Manas Neuropsychiatry Hospital — Location"
                src={MAPS_EMBED_URL}
                width="100%"
                height="100%"
                style={{ border: 0, filter: "grayscale(0.2) contrast(0.95)" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
            <a
              href="https://maps.app.goo.gl/xNXxF1g2FT5C1smKA"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-map-directions"
              className="mt-3 inline-flex items-center gap-1 text-xs uppercase tracking-[0.18em] text-[#E2C044] hover:text-[#E07A5F] transition-colors"
            >
              {g("Get directions →")}
            </a>
          </div>
        </div>

        <div className="border-t border-[#FDFBF7]/10 mt-16 pt-8 flex flex-col md:flex-row gap-4 justify-between text-sm text-[#FDFBF7]/50">
          <p>© {new Date().getFullYear()} Manas Neuropsychiatry Hospital. {g("All rights reserved.")}</p>
          <p>{g("Bhavnagar, Gujarat — India")}</p>
        </div>
      </div>
    </footer>
  );
}
